
import { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { WellnessCard } from "@/components/WellnessCard";
import { ProgressChart } from "@/components/ProgressChart";
import { ChatAssistant } from "@/components/ChatAssistant";
import { VitaContentSection } from "@/components/VitaContentSection";
import { BarChart, Utensils, Dumbbell, Pill, Brain, LineChart, StickyNote, Moon, LayoutDashboard } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { SleepSection } from "@/components/SleepSection";
import { OverviewSection } from "@/components/wellness/OverviewSection";
import NutritionDashboard from "@/components/wellness/NutritionDashboard";
import { NotesEditor } from "@/components/wellness/NotesEditor";
import { useWellness } from "@/context/WellnessContext";

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const { 
    calculateNutritionScore, 
    calculateFitnessScore, 
    calculateSupplementScore, 
    calculateMindfulnessScore,
    getSleepScore
  } = useWellness();
  
  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-primary/10 py-8 px-4">
        <div className="container mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-display font-bold tracking-tight mb-2">Your Wellness Dashboard</h1>
            <p className="text-muted-foreground">Track your progress and manage your wellness journey.</p>
          </div>

          <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid grid-cols-4 md:grid-cols-8 w-full bg-card bg-opacity-50 p-1">
              <TabsTrigger value="overview" data-value="overview" className="flex items-center gap-2">
                <LayoutDashboard className="h-4 w-4" />
                <span className="hidden md:inline">Overview</span>
              </TabsTrigger>
              <TabsTrigger value="nutrition" data-value="nutrition" className="flex items-center gap-2">
                <Utensils className="h-4 w-4" />
                <span className="hidden md:inline">Nutrition</span>
              </TabsTrigger>
              <TabsTrigger value="exercise" data-value="exercise" className="flex items-center gap-2">
                <Dumbbell className="h-4 w-4" />
                <span className="hidden md:inline">Exercise</span>
              </TabsTrigger>
              <TabsTrigger value="supplements" data-value="supplements" className="flex items-center gap-2">
                <Pill className="h-4 w-4" />
                <span className="hidden md:inline">Supplements</span>
              </TabsTrigger>
              <TabsTrigger value="mindfulness" data-value="mindfulness" className="flex items-center gap-2">
                <Brain className="h-4 w-4" />
                <span className="hidden md:inline">Mindfulness</span>
              </TabsTrigger>
              <TabsTrigger value="sleep" data-value="sleep" className="flex items-center gap-2">
                <Moon className="h-4 w-4" />
                <span className="hidden md:inline">Sleep</span>
              </TabsTrigger>
              <TabsTrigger value="progress" data-value="progress" className="flex items-center gap-2">
                <LineChart className="h-4 w-4" />
                <span className="hidden md:inline">Progress</span>
              </TabsTrigger>
              <TabsTrigger value="notes" data-value="notes" className="flex items-center gap-2">
                <StickyNote className="h-4 w-4" />
                <span className="hidden md:inline">Notes</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <OverviewSection />
            </TabsContent>

            <TabsContent value="nutrition" className="space-y-6">
              <NutritionDashboard />
            </TabsContent>

            <TabsContent value="exercise" className="space-y-6">
              <WellnessCard 
                title="Exercise Overview" 
                icon={<Dumbbell className="h-5 w-5" />} 
                category="exercise"
              >
                <div className="flex flex-col items-center justify-center py-6">
                  <div className="text-5xl font-bold mb-2">{calculateFitnessScore()}</div>
                  <p className="text-muted-foreground text-sm">
                    {calculateFitnessScore() > 80 
                      ? "Your fitness score is excellent!" 
                      : calculateFitnessScore() > 60 
                        ? "Your fitness score is good!" 
                        : calculateFitnessScore() > 0 
                          ? "Your fitness score is improving!" 
                          : "Start tracking your exercises to see your fitness score!"}
                  </p>
                </div>
              </WellnessCard>
              <Card>
                <CardHeader>
                  <CardTitle>Your Exercise Plan</CardTitle>
                  <CardDescription>Current recommended exercise routines</CardDescription>
                </CardHeader>
                <CardContent>
                  {calculateFitnessScore() > 0 ? (
                    <div>
                      {/* Exercise plan content */}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="bg-primary/10 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <Dumbbell className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-xl font-medium">Start Your Fitness Journey</h3>
                      <p className="text-muted-foreground mt-2 mb-4">
                        Begin tracking your exercises to improve your fitness and wellbeing.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
              <VitaContentSection category="exercise" title="Workouts from Vita" />
            </TabsContent>

            <TabsContent value="supplements" className="space-y-6">
              <WellnessCard 
                title="Supplements Overview" 
                icon={<Pill className="h-5 w-5" />} 
                category="supplements"
              >
                <div className="flex flex-col items-center justify-center py-6">
                  <div className="text-5xl font-bold mb-2">{calculateSupplementScore()}</div>
                  <p className="text-muted-foreground text-sm">
                    {calculateSupplementScore() > 80 
                      ? "Your supplement plan is well-balanced!" 
                      : calculateSupplementScore() > 60 
                        ? "Your supplement intake is good!" 
                        : calculateSupplementScore() > 0 
                          ? "Your supplement tracking is improving!" 
                          : "Start tracking your supplements to see your score!"}
                  </p>
                </div>
              </WellnessCard>
              <Card>
                <CardHeader>
                  <CardTitle>Your Supplement Plan</CardTitle>
                  <CardDescription>Current recommended supplements</CardDescription>
                </CardHeader>
                <CardContent>
                  {calculateSupplementScore() > 0 ? (
                    <div>
                      {/* Supplement plan content */}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="bg-primary/10 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <Pill className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-xl font-medium">Start Your Supplement Routine</h3>
                      <p className="text-muted-foreground mt-2 mb-4">
                        Begin tracking your supplements to enhance your wellness journey.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
              <VitaContentSection category="supplements" title="Supplement Plans from Vita" />
            </TabsContent>

            <TabsContent value="mindfulness" className="space-y-6">
              <WellnessCard 
                title="Mindfulness Overview" 
                icon={<Brain className="h-5 w-5" />} 
                category="mindfulness"
              >
                <div className="flex flex-col items-center justify-center py-6">
                  <div className="text-5xl font-bold mb-2">{calculateMindfulnessScore()}</div>
                  <p className="text-muted-foreground text-sm">
                    {calculateMindfulnessScore() > 80 
                      ? "Your mindfulness practice is excellent!" 
                      : calculateMindfulnessScore() > 60 
                        ? "Your mindfulness score is good!" 
                        : calculateMindfulnessScore() > 0 
                          ? "Your mindfulness score is improving!" 
                          : "Start tracking your mindfulness to see your score!"}
                  </p>
                </div>
              </WellnessCard>
              <Card>
                <CardHeader>
                  <CardTitle>Your Mindfulness Plan</CardTitle>
                  <CardDescription>Current recommended mindfulness practices</CardDescription>
                </CardHeader>
                <CardContent>
                  {calculateMindfulnessScore() > 0 ? (
                    <div>
                      {/* Mindfulness plan content */}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="bg-primary/10 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <Brain className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-xl font-medium">Begin Your Mindfulness Journey</h3>
                      <p className="text-muted-foreground mt-2 mb-4">
                        Start tracking your mindfulness practices for improved mental wellbeing.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
              <VitaContentSection category="mindfulness" title="Mindfulness Practices from Vita" />
            </TabsContent>

            <TabsContent value="sleep" className="space-y-6">
              <WellnessCard 
                title="Sleep Overview" 
                icon={<Moon className="h-5 w-5" />} 
                category="sleep"
              >
                <div className="flex flex-col items-center justify-center py-6">
                  <div className="text-5xl font-bold mb-2">{getSleepScore()}</div>
                  <p className="text-muted-foreground text-sm">
                    {getSleepScore() > 80 
                      ? "Your sleep quality is excellent!" 
                      : getSleepScore() > 60 
                        ? "Your sleep quality is good!" 
                        : "Your sleep quality could use improvement"}
                  </p>
                </div>
              </WellnessCard>
              <Card>
                <CardHeader>
                  <CardTitle>Your Sleep Data</CardTitle>
                  <CardDescription>Sleep duration and quality metrics</CardDescription>
                </CardHeader>
                <CardContent>
                  <SleepSection />
                </CardContent>
              </Card>
              <VitaContentSection category="sleep" title="Sleep Recommendations from Vita" />
            </TabsContent>

            <TabsContent value="progress" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Your Progress</CardTitle>
                  <CardDescription>Track your wellness journey over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <ProgressChart />
                </CardContent>
              </Card>
              <WellnessCard 
                title="Overall Wellness Score" 
                icon={<BarChart className="h-5 w-5" />} 
                category="overall"
              >
                <div className="flex flex-col items-center justify-center py-6">
                  <div className="text-5xl font-bold mb-2">{calculateNutritionScore()}</div>
                  <p className="text-muted-foreground text-sm">Your wellness score is great!</p>
                </div>
              </WellnessCard>
            </TabsContent>

            <TabsContent value="notes" className="space-y-6">
              <NotesEditor />
            </TabsContent>
          </Tabs>

          <div className="mt-8">
            <ChatAssistant />
          </div>
        </div>
      </div>
    </>
  );
}
